import React from 'react';
import { motion } from 'framer-motion';
import { Users, Target, Sparkles, Award, Zap, Shield } from 'lucide-react';
import { Heading } from '../typography/Heading';
import { Text } from '../typography/Text';

const features = [
  {
    icon: Sparkles,
    title: "AI-Powered Generation",
    description: "Our advanced AI technology creates personalized cover letters tailored to your experience and the job requirements."
  },
  {
    icon: Target,
    title: "Industry-Specific",
    description: "Customized content for different industries and job roles, ensuring relevance and impact."
  },
  {
    icon: Zap,
    title: "Instant Results",
    description: "Generate professional cover letters in seconds, saving you valuable time in your job search."
  },
  {
    icon: Shield,
    title: "Data Security",
    description: "Your information is protected with enterprise-grade security and encryption."
  }
];

const stats = [
  { number: "500K+", label: "Cover Letters Generated" },
  { number: "95%", label: "Success Rate" },
  { number: "150+", label: "Industries Covered" },
  { number: "24/7", label: "AI Assistance" }
];

export function About() {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Heading level={1} align="center" className="mb-6">
              Revolutionizing Job Applications with AI
            </Heading>
            <Text size="lg" align="center" className="text-slate-light max-w-2xl mx-auto">
              hiiroo combines artificial intelligence with proven writing techniques 
              to help job seekers create compelling applications that get noticed by employers.
            </Text>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Heading level={2} align="center" className="mb-8">Our Mission</Heading>
            <Text size="lg" align="center" className="text-slate-light">
              We're on a mission to democratize the job application process by making it easy 
              for everyone to create professional, compelling applications. Our AI-powered platform 
              helps job seekers present their best selves to potential employers, increasing their 
              chances of landing their dream jobs.
            </Text>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <Heading level={2} align="center" className="mb-12">Why Choose Us</Heading>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <Heading level={3} className="mb-2">{feature.title}</Heading>
                  <Text className="text-slate-light">{feature.description}</Text>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-primary">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl font-bold text-white mb-2">{stat.number}</div>
                <Text className="text-white/80">{stat.label}</Text>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Heading level={2} className="mb-6">Meet Our Team</Heading>
            <Text size="lg" className="text-slate-light mb-8">
              We're a dedicated team of AI specialists, career experts, and developers working 
              together to revolutionize the job application process. Our diverse backgrounds and 
              expertise enable us to create innovative solutions that help job seekers succeed.
            </Text>
            <div className="flex items-center justify-center">
              <Users className="w-16 h-16 text-primary" />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}